import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Heart, ChatCircle, Image } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Lightbox from '../components/Lightbox';
import UserAvatar from '../components/UserAvatar';

import { API_URL } from '../config';

const GalleryPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/community/gallery`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPostDetails = async (postId) => {
    try {
      const response = await axios.get(`${API_URL}/api/community/posts/${postId}`);
      setSelectedPost(response.data);
    } catch (error) {
      console.error('Error loading post:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-black text-3xl uppercase text-white mb-2" data-testid="gallery-title">
            Gallery
          </h1>
          <p className="text-[#8892B0]">Chart screenshots and images shared by the community</p>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="text-center py-12 text-[#8892B0]">Loading gallery...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 bg-[#131313] border border-white/5">
            <Image size={56} className="mx-auto text-[#5C6B82] mb-4" />
            <p className="text-[#8892B0] mb-2 text-lg">No images shared yet</p>
            <p className="text-sm text-[#5C6B82]">Head to the Community page and attach an image to your post</p>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4" data-testid="gallery-page-grid">
            {posts.map((post) => (
              <div
                key={post.id}
                className="break-inside-avoid mb-4 bg-[#131313] border border-white/5 overflow-hidden group cursor-pointer hover:border-[#007AFF]/50 transition-all hover:shadow-lg hover:shadow-[#007AFF]/5"
                data-testid={`gallery-page-item-${post.id}`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={post.image_url}
                    alt={post.content?.slice(0, 50)}
                    className="w-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                    onClick={() => { setLightboxSrc(post.image_url); setLightboxAlt(post.content?.slice(0, 50)); }}
                  />
                </div>
                <div className="p-4" onClick={() => loadPostDetails(post.id)}>
                  <p className="text-[#8892B0] text-sm line-clamp-2 mb-3">{post.content}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserAvatar src={post.user_avatar} name={post.user_name} size={20} />
                      <span className="text-[#5C6B82] text-xs">{post.user_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-[#8892B0] text-xs">
                        <Heart size={12} /> {post.likes}
                      </span>
                      <span className="flex items-center gap-1 text-[#8892B0] text-xs">
                        <ChatCircle size={12} /> {post.comments_count}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Post Detail Dialog */}
        <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
          <DialogContent className="bg-[#131313] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedPost && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <UserAvatar src={selectedPost.user_avatar} name={selectedPost.user_name} size={36} />
                    <div>
                      <span className="text-white font-bold">{selectedPost.user_name}</span>
                      <p className="text-[#5C6B82] text-xs">
                        {new Date(selectedPost.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <DialogTitle className="sr-only">Post by {selectedPost.user_name}</DialogTitle>
                </DialogHeader>
                <div className="mt-2">
                  <p className="text-[#CCD6F6] whitespace-pre-wrap">{selectedPost.content}</p>
                  {selectedPost.image_url && (
                    <img
                      src={selectedPost.image_url}
                      alt="Post attachment"
                      className="mt-4 max-h-[500px] rounded border border-white/10 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => { setLightboxSrc(selectedPost.image_url); setLightboxAlt(selectedPost.content?.slice(0, 50)); }}
                      data-testid="gallery-detail-image"
                    />
                  )}
                  {/* Comments */}
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <h4 className="font-bold text-white mb-4">Comments ({selectedPost.comments?.length || 0})</h4>
                    {selectedPost.comments?.map((comment) => (
                      <div key={comment.id} className="bg-[#0A0A0A] p-4 mb-3 border border-white/5">
                        <div className="flex items-start gap-3">
                          <UserAvatar src={comment.user_avatar} name={comment.user_name} size={28} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-bold text-white">{comment.user_name}</span>
                              <span className="text-xs text-[#5C6B82]">
                                {new Date(comment.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-[#8892B0] text-sm">{comment.content}</p>
                            {comment.image_url && (
                              <img
                                src={comment.image_url}
                                alt="Comment attachment"
                                className="mt-2 max-h-48 rounded border border-white/10 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => { setLightboxSrc(comment.image_url); setLightboxAlt('Comment image'); }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Lightbox */}
        {lightboxSrc && (
          <Lightbox
            src={lightboxSrc}
            alt={lightboxAlt}
            onClose={() => { setLightboxSrc(null); setLightboxAlt(''); }}
          />
        )}
      </div>
    </div>
  );
};

export default GalleryPage;
